import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiHome,
  FiBell,
  FiUsers,
  FiBriefcase,
  FiLayers,
  FiUser,
  FiCreditCard,
  FiSettings,
  FiLogOut,
  FiCalendar,
  FiClock,
  FiFileText,
  FiDollarSign,
  FiBook,
  FiCheckSquare,
  FiShield,
  FiTrendingUp,
  FiUserPlus,
  FiUserCheck,
  FiUserX,
  FiHeart,
  FiAward,
  FiMessageSquare,
  FiAlertCircle,
  FiChevronDown,
} from "react-icons/fi";
import { OrganizationSwitcher } from "@clerk/clerk-react";
import { useAuth } from "../../contexts/AuthContext";
import Avatar from "../ui/Avatar";
import BrandLogo from "../ui/BrandLogo";
import { isCollaborateurUser } from "../../utils/roles";

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  hash?: string;
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

interface NavSection {
  title: string;
  items?: NavItem[];
  groups?: NavGroup[];
}

const RH_SECTIONS: NavSection[] = [
  {
    title: "Tableau de bord",
    items: [
      { path: "/dashboard", label: "Accueil", icon: <FiHome size={18} /> },
      {
        path: "/dashboard",
        label: "Notifications",
        icon: <FiBell size={18} />,
        hash: "#notifications",
      },
    ],
  },
  {
    title: "Mon équipe",
    groups: [
      {
        id: "recruter",
        label: "Recruter",
        items: [
          {
            path: "/employes",
            label: "Recrutement",
            icon: <FiUserPlus size={16} />,
            hash: "#recrutement",
          },
          {
            path: "/employes",
            label: "Intégration",
            icon: <FiUserCheck size={16} />,
            hash: "#integration",
          },
        ],
      },
      {
        id: "structurer",
        label: "Structurer l'équipe",
        items: [
          {
            path: "/employes",
            label: "Mes effectifs",
            icon: <FiUsers size={16} />,
            hash: "#effectifs",
          },
          {
            path: "/employes",
            label: "Dossiers salariés",
            icon: <FiUser size={16} />,
            hash: "#dossiers",
          },
          {
            path: "/documents",
            label: "Contrats",
            icon: <FiFileText size={16} />,
          },
          {
            path: "/absences-rh",
            label: "Temps & absences",
            icon: <FiCalendar size={16} />,
          },
          {
            path: "/feuilles-temps-rh",
            label: "Feuilles de temps",
            icon: <FiClock size={16} />,
          },
          {
            path: "/bulletins-paie",
            label: "Rémunération",
            icon: <FiCreditCard size={16} />,
          },
          {
            path: "/notes-de-frais-rh",
            label: "Notes de frais",
            icon: <FiDollarSign size={16} />,
          },
          {
            path: "/employes",
            label: "Suivi médical",
            icon: <FiHeart size={16} />,
            hash: "#medical",
          },
          {
            path: "/employes",
            label: "Formation",
            icon: <FiAward size={16} />,
            hash: "#formation",
          },
          {
            path: "/employes",
            label: "Entretiens & évolution",
            icon: <FiMessageSquare size={16} />,
            hash: "#entretiens",
          },
          {
            path: "/employes",
            label: "Disciplinaire",
            icon: <FiAlertCircle size={16} />,
            hash: "#disciplinaire",
          },
        ],
      },
      {
        id: "depart",
        label: "Accompagner le départ",
        items: [
          {
            path: "/employes",
            label: "Départ",
            icon: <FiUserX size={16} />,
            hash: "#depart",
          },
        ],
      },
    ],
  },
  {
    title: "Sécuriser l’entreprise",
    items: [
      {
        path: "/obligations",
        label: "Obligations",
        icon: <FiCheckSquare size={18} />,
      },
      { path: "/audit", label: "Audit & QVT", icon: <FiShield size={18} /> },
      {
        path: "/documents",
        label: "Documents RH",
        icon: <FiFileText size={18} />,
      },
    ],
  },
  {
    title: "Développer l’entreprise",
    items: [
      {
        path: "/structure",
        label: "Mon entreprise",
        icon: <FiBriefcase size={18} />,
      },
      {
        path: "/structure",
        label: "Ma structure",
        icon: <FiLayers size={18} />,
        hash: "#structure",
      },
      {
        path: "/guides",
        label: "Progression RH",
        icon: <FiTrendingUp size={18} />,
      },
      { path: "/marketplace", label: "Experts", icon: <FiBook size={18} /> },
      {
        path: "/parametres",
        label: "Paramètres",
        icon: <FiSettings size={18} />,
      },
    ],
  },
];

const COLLAB_NAV: NavItem[] = [
  { path: "/dashboard", label: "Accueil", icon: <FiHome size={18} /> },
  { path: "/absences", label: "Absences", icon: <FiCalendar size={18} /> },
  {
    path: "/mes-temps",
    label: "Feuilles de temps",
    icon: <FiClock size={18} />,
  },
  {
    path: "/notes-de-frais",
    label: "Notes de frais",
    icon: <FiFileText size={18} />,
  },
  { path: "/annuaire", label: "Annuaire", icon: <FiBook size={18} /> },
  { path: "/organigramme", label: "Organigramme", icon: <FiUsers size={18} /> },
  { path: "/ma-paie", label: "Ma paie", icon: <FiCreditCard size={18} /> },
  { path: "/parametres", label: "Paramètres", icon: <FiSettings size={18} /> },
];

const SIDEBAR_WIDTH = 248;

const ROLE_LABELS: Record<string, string> = {
  rh: "RH",
  admin: "RH",
  dirigeant: "RH",
  manager: "RH",
  collaborateur: "Collaborateur",
  salarie: "Collaborateur",
};

const EMPLOYE_HASH_ACTIVE: Record<string, string[]> = {
  Recrutement: ["#recrutement", "#recruter"],
  Intégration: ["#integration"],
  "Mes effectifs": ["#effectifs", ""],
  "Dossiers salariés": ["#dossiers", "#annuaire"],
  "Suivi médical": ["#medical"],
  Formation: ["#formation"],
  "Entretiens & évolution": ["#entretiens"],
  Disciplinaire: ["#disciplinaire"],
  Départ: ["#depart"],
};

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isCollab = isCollaborateurUser(user);

  const isItemActive = (item: NavItem) => {
    const { path, label, hash } = item;
    if (isCollab) {
      return location.pathname === path;
    }
    if (label === "Notifications") {
      return (
        location.pathname === "/dashboard" && location.hash === "#notifications"
      );
    }
    if (label === "Accueil") {
      return (
        location.pathname === "/dashboard" && location.hash !== "#notifications"
      );
    }
    if (label === "Mon entreprise") {
      return (
        location.pathname === "/structure" && location.hash !== "#structure"
      );
    }
    if (label === "Ma structure") {
      return (
        location.pathname === "/structure" && location.hash === "#structure"
      );
    }
    if (label === "Contrats") {
      return location.pathname.startsWith("/documents");
    }

    const employeHashes = EMPLOYE_HASH_ACTIVE[label];
    if (employeHashes && path.startsWith("/employes")) {
      if (!location.pathname.startsWith("/employes")) return false;
      if (location.pathname.match(/^\/employes\/[^/]+$/)) {
        return label === "Dossiers salariés";
      }
      const currentHash = location.hash || "";
      if (label === "Mes effectifs") {
        return (
          location.pathname === "/employes" &&
          (currentHash === "" || currentHash === "#effectifs")
        );
      }
      return employeHashes.includes(currentHash);
    }

    if (hash) {
      return location.pathname === path && location.hash === hash;
    }
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const activeGroupIds = useMemo(() => {
    const ids: string[] = [];
    RH_SECTIONS.forEach((section) => {
      section.groups?.forEach((group) => {
        if (group.items.some((item) => isItemActive(item))) {
          ids.push(group.id);
        }
      });
    });
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    recruter: true,
    structurer: true,
    depart: true,
  });

  useEffect(() => {
    if (activeGroupIds.length === 0) return;
    setOpenGroups((prev) => {
      const next = { ...prev };
      activeGroupIds.forEach((id) => {
        next[id] = true;
      });
      return next;
    });
  }, [activeGroupIds]);

  const getNavTarget = (
    item: NavItem,
  ): string | { pathname: string; hash: string } => {
    if (item.hash) {
      return {
        pathname: item.path,
        hash: item.hash.startsWith("#") ? item.hash : `#${item.hash}`,
      };
    }
    if (
      item.label === "Accueil" ||
      item.label === "Mon entreprise" ||
      item.label === "Mes effectifs"
    ) {
      return { pathname: item.path, hash: "" };
    }
    return item.path;
  };

  const renderLink = (item: NavItem, nested = false) => {
    const isActive = isItemActive(item);
    const target = getNavTarget(item);
    return (
      <Link
        key={`${item.path}-${item.label}-${item.hash || ""}`}
        to={target}
        aria-current={isActive ? "page" : undefined}
        onClick={(e) => {
          if (typeof target !== "object") return;
          if (location.pathname !== target.pathname) return;
          const nextHash = target.hash || "";
          if (location.hash === nextHash) {
            e.preventDefault();
            navigate(
              { pathname: target.pathname, hash: nextHash },
              { replace: true },
            );
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: nested ? "0.55rem" : "0.75rem",
          height: nested ? 32 : 36,
          padding: nested ? "0 0.55rem 0 0.85rem" : "0 0.75rem",
          borderRadius: "var(--radius-md)",
          fontSize: nested ? "0.8rem" : "var(--font-size-sm)",
          fontWeight: isActive ? 600 : 400,
          color: isActive
            ? "var(--color-brand)"
            : "var(--color-text-secondary)",
          background: isActive ? "var(--color-brand-light)" : "transparent",
          textDecoration: "none",
          transition: "all var(--transition-fast)",
          position: "relative",
        }}
      >
        {isActive && (
          <motion.div
            layoutId="sidebar-indicator"
            style={{
              position: "absolute",
              left: 0,
              top: "20%",
              bottom: "20%",
              width: 3,
              borderRadius: 2,
              background: "var(--color-brand)",
            }}
          />
        )}
        <span style={{ display: "flex", color: "inherit", flexShrink: 0 }}>
          {item.icon}
        </span>
        <span
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.label}
        </span>
      </Link>
    );
  };

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <aside
      aria-label="Navigation principale"
      style={{
        width: SIDEBAR_WIDTH,
        height: "100vh",
        background: "var(--color-surface)",
        borderRight: "1px solid var(--color-border-light)",
        display: "flex",
        flexDirection: "column",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          height: "var(--header-height)",
          padding: "0 1.25rem",
          borderBottom: "1px solid var(--color-border-light)",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <BrandLogo expanded size="sm" />
      </div>

      <div style={{ padding: "0.75rem 0.75rem 0", flexShrink: 0 }}>
        <OrganizationSwitcher
          hidePersonal
          afterCreateOrganizationUrl="/dashboard"
          afterSelectOrganizationUrl="/dashboard"
          appearance={{
            elements: {
              rootBox: { width: "100%" },
              organizationSwitcherTrigger: {
                width: "100%",
                justifyContent: "space-between",
                padding: "0.5rem 0.65rem",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--color-border-light)",
                background: "var(--color-bg)",
              },
            },
          }}
        />
      </div>

      <nav style={{ flex: 1, padding: "0.75rem", overflowY: "auto" }}>
        {isCollab ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.125rem",
            }}
          >
            {COLLAB_NAV.map((item) => renderLink(item))}
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            {RH_SECTIONS.map((section) => (
              <div key={section.title}>
                <p
                  style={{
                    margin: "0 0 0.4rem 0.5rem",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    color: "var(--color-text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  {section.title}
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.15rem",
                  }}
                >
                  {section.items?.map((item) => renderLink(item))}
                  {section.groups?.map((group) => {
                    const open = openGroups[group.id] !== false;
                    return (
                      <div key={group.id}>
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.id)}
                          aria-expanded={open}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.5rem",
                            height: 34,
                            padding: "0 0.65rem",
                            border: "none",
                            borderRadius: "var(--radius-md)",
                            background: "transparent",
                            color: "var(--color-text)",
                            fontFamily: "var(--font-family)",
                            fontSize: "0.8rem",
                            fontWeight: 650,
                            cursor: "pointer",
                          }}
                        >
                          <span>{group.label}</span>
                          <FiChevronDown
                            size={14}
                            style={{
                              transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                              transition: "transform 0.15s ease",
                              color: "var(--color-text-muted)",
                            }}
                          />
                        </button>
                        {open && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.05rem",
                              marginBottom: "0.25rem",
                              borderLeft: "1px solid var(--color-border-light)",
                              marginLeft: "0.7rem",
                              paddingLeft: "0.2rem",
                            }}
                          >
                            {group.items.map((item) => renderLink(item, true))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </nav>

      <div
        style={{
          padding: "0.85rem",
          borderTop: "1px solid var(--color-border-light)",
          display: "flex",
          alignItems: "center",
          gap: "0.65rem",
        }}
      >
        <Avatar
          name={user ? `${user.first_name} ${user.last_name}` : ""}
          size={36}
          src={user?.avatar_url}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "var(--font-size-sm)",
              fontWeight: 600,
              color: "var(--color-text)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {user ? `${user.first_name} ${user.last_name}` : ""}
          </div>
          <div style={{ fontSize: "0.7rem", color: "var(--color-text-muted)" }}>
            {ROLE_LABELS[user?.role || ""] || user?.role || ""}
          </div>
        </div>
        <button
          type="button"
          onClick={logout}
          aria-label="Se déconnecter"
          style={{
            background: "none",
            border: "none",
            color: "var(--color-text-muted)",
            cursor: "pointer",
            padding: "0.35rem",
            display: "flex",
          }}
        >
          <FiLogOut size={16} />
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
